import React, { useEffect, useMemo, useRef, useState } from 'react'

import { PaidMarketingHomeStyle } from './Home.styled'
import { CheqLoader, useFrontEndEventTrackingService } from '@clickcease/cc-client-components-lib'
import { useGetBotMitigationData } from 'Features/home/api/getProductData'

import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { useProductsStatus } from 'Features/MainWrapper/api/getProductsStatus'
import { InsightsCard } from 'Features/home/components/Insights/InsightsCard'
import { ClickceaseMigrationBanner } from 'Features/ClickceaseMigrationBanner/ClickceaseMigrationBanner'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { useMigrationDisplay } from 'Hooks/useMigrationDisplay'
import { useOverageUsageMessage } from 'Features/home/hooks/OverageUsageMessage/useOverageUsageMessage'
import { useVisitsSummary } from 'Features/home/api/getVisitsSummary'
import { VisitsSummary } from 'Features/home/api/types'
import { getCountryValueByCountryLabel, toUnixDateRangeNew } from 'Utils/dates'
import { subWeeks, startOfToday, differenceInDays } from 'date-fns'
import { Link, useLocation } from 'react-router-dom'
import { NewInvalidVisitsTrendsAndThreatGroups } from 'Features/home/components/InvalidVisitsTrendsAndThreatGroups/NewInvalidVisitsTrendsAndThreatGroups'
import { NewOverallDomainsPerformance } from 'Features/home/components/OverallDomainsPreformance/NewOverallDomainsPerformance'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useTrends } from 'Features/home/api/getTrends'
import { useThreatGroups } from 'Features/home/api/getThreatGroups'
import { EssentialsModules } from 'Features/home/components/EssentialsModules/EssentialsModules'
import { useSplit } from 'Hooks/useSplit'
import { Connection } from 'Types/PlatformsConnections'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { TOASTS } from 'Components/Elements/Toaster/Toast.models'
import { useGetPlatformConnections } from 'Features/domainsManagement/api/getPlatformConnections'
import { useGoogleDisconnectionModal } from 'Hooks/split/useGoogleDisconnectionModal'
import { EditBillingDetails } from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/EditBillingDetailsModal'

interface LocationStateType {
  fromLoginPage?: boolean
}

export const NewHome = () => {
  const domainsTable = useRef(null)
  const googleDisconnectedSplit = useGoogleDisconnectionModal()
  const { setModalToShow, showSystemMessage, removeSystemMessage, systemMessages } = useAppStore()
  const { timezoneCountry, setPaymentMethod, paymentMethod, accountId, userId, email } =
    useUserStore()
  const isPlatformStatusOn = useSplit('CE_PLATFORM_STATUS')
  const isUpdateBillingModalSplitOn = useSplit('CE_SHOW_BILLING_POPUP')
  const location = useLocation()
  const locationState = location.state as LocationStateType
  const { data: platformConnections } = useGetPlatformConnections()
  const selectedTimezone = useMemo(() => {
    return getCountryValueByCountryLabel(timezoneCountry)
  }, [timezoneCountry])

  const { isMigratedAccount, isMigrationFinished } = useMigrationDisplay()
  const { data: planDetails, isLoading: plansIsLoading } = useGetPlansDetails()
  useOverageUsageMessage()
  if (!paymentMethod && planDetails && 'paymentMethod' in planDetails) {
    setPaymentMethod(planDetails.paymentMethod || '')
  }

  const [showEditBillingModal, setShowEditBillingModal] = useState<boolean>(
    planDetails?.billingUpdated === false
  )
  const { track } = useFrontEndEventTrackingService()

  useEffect(() => {
    if (locationState?.fromLoginPage && googleDisconnectedSplit) {
      shouldShowGoogleDisconnectedModal(platformConnections || [])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformConnections, accountId, locationState?.fromLoginPage])

  const googleConnectedMessageClicked = () => {
    const filteredId = systemMessages.find((msg) =>
      msg.i18config.i18nKey.includes('system_messages.GOOGLE_ACCOUNT_DISCONNECTED')
    )?.id
    removeSystemMessage(filteredId)
  }

  const shouldShowGoogleDisconnectedModal = (connections: Array<Connection>) => {
    //Show modal/message if disconnected within the last 7 days
    const disconnectedGoogleAccounts: Array<string> = []
    let tokenRevokedDate = ''
    connections?.forEach((connection) => {
      if (connection?.tokenStatus === 'Disconnected') {
        disconnectedGoogleAccounts.push(connection?.displayId)
        tokenRevokedDate = connection?.tokenRevokedDate || ''
      }
    })

    if (disconnectedGoogleAccounts?.length > 0) {
      if (!localStorage.getItem('googleDisconnectedModalSeen')) {
        setModalToShow(GLOBAL_MODALS.GOOGLE_DISCONNECTED, disconnectedGoogleAccounts || [])
        localStorage.setItem('googleDisconnectedModalSeen', 'true')
      } else if (differenceInDays(new Date(), new Date(tokenRevokedDate || '')) <= 7) {
        //show toaster message if disconnected within the last 7 days
        showSystemMessage(
          {
            i18nKey: `system_messages.GOOGLE_ACCOUNT_DISCONNECTED`,
            components: {
              action: (
                <Link to="/paid-marketing/platforms" onClick={googleConnectedMessageClicked} />
              ),
            },
          },
          TOASTS.failure
        )
      }
    }
  }

  const { isLoading: domainsIsLoading } = useGetDomains()

  const today = startOfToday()
  const dateRange = {
    from: subWeeks(today, 1),
    to: today,
  }

  const serverDateConversion = toUnixDateRangeNew(timezoneCountry, dateRange)

  const { isLoading: limitsAreLoading } = useGetPackagingLimits()
  const { isLoading: productsAreLoading } = useProductsStatus()
  const { data: botMitigationData, isLoading: bmDataIsLoading } = useGetBotMitigationData({})

  const { data: visitsSummary, isLoading: pmDataIsLoding } = useVisitsSummary({
    domains: [],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const { isLoading: trendsAreLoading } = useTrends({
    domains: [],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const { isLoading: threatGroupsAreLoading } = useThreatGroups({
    domains: [],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const productsDataIsLoading =
    limitsAreLoading ||
    productsAreLoading ||
    bmDataIsLoading ||
    domainsIsLoading ||
    plansIsLoading ||
    pmDataIsLoding ||
    trendsAreLoading ||
    threatGroupsAreLoading

  const visitsData = Array.isArray(visitsSummary) ? visitsSummary?.[0] : new VisitsSummary()

  const invalidPercentage = useMemo(() => {
    if (!visitsData?.paidInvalidTraffic || !visitsData?.paidTraffic) {
      return 0
    } else {
      return Number(((visitsData?.paidInvalidTraffic / visitsData?.paidTraffic) * 100).toFixed(2))
    }
  }, [visitsData.paidInvalidTraffic, visitsData?.paidTraffic])

  const bmInvalidRate = botMitigationData?.summary?.total?.invalidRate || 0
  const bmIpBlocked = botMitigationData?.summary?.total?.totalBlocked || 0

  const bmInvalidPercentage = Math.round(bmInvalidRate * 100) / 100

  const showMigrationBanner =
    isMigratedAccount && !isMigrationFinished && planDetails?.trial?.active

  const handleUpdateBillingModalClose = () => {
    setShowEditBillingModal(false)
    track(['segment'], 'update_billing_modal_skipped', {
      account_id: accountId,
      user_id: userId,
      email,
      country: timezoneCountry,
      has_visits: visitsData?.visits > 0,
    })
  }

  return (
    <div id={'home-page'} style={{ height: '100%' }}>
      {showMigrationBanner && <ClickceaseMigrationBanner />}
      {productsDataIsLoading ? (
        <CheqLoader />
      ) : (
        <PaidMarketingHomeStyle noPlatformStatus={!isPlatformStatusOn}>
          <EssentialsModules
            pmBlocked={visitsData?.paidInvalidTraffic || 0}
            pmInvalidPercentage={invalidPercentage}
            pmInvalidRate={invalidPercentage}
            bmBlocked={bmIpBlocked}
            bmInvalidPercentage={bmInvalidPercentage}
            bmInvalidRate={bmInvalidRate}
            isLoading={productsDataIsLoading}
            domainsTableRef={domainsTable}
          />
          <InsightsCard />
          <NewInvalidVisitsTrendsAndThreatGroups />
          <NewOverallDomainsPerformance innerRef={domainsTable} />
          {showEditBillingModal && isUpdateBillingModalSplitOn && (
            <EditBillingDetails
              isOpen={showEditBillingModal}
              onOpenChange={() => handleUpdateBillingModalClose()}
              source={'home'}
            />
          )}
        </PaidMarketingHomeStyle>
      )}
    </div>
  )
}
